import React from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'

const TermsOfService = () => {
  const { t } = useTranslation();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 pt-20">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="bg-white/5 backdrop-blur-lg rounded-3xl p-8 border border-white/10"
        >
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-4xl font-bold text-white mb-8 text-center"
          >
            {t('legal.termsOfService.title')}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="prose prose-invert max-w-none text-gray-300 space-y-6"
          >
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. Acceptance of Terms</h2>
              <p className="mb-4">
                Welcome to Konvo AI, an innovative AI Voice Assistant service ("Service") provided by Konvo AI ("Company," "we," "us," or "our"). By accessing our website at www.konvoai.tech or using our Service, you ("User," "you," or "your") agree to be bound by these Terms of Service ("Terms"). These Terms constitute a legally binding agreement between you and Konvo AI.
              </p>
              <p className="mb-4">
                If you are using the Service on behalf of an organization, you are agreeing to these Terms for that organization and you represent that you have the authority to bind that organization to these Terms. In that case, "you" and "your" will refer to that organization.
              </p>
              <p>
                Please read these Terms carefully. If you do not agree to all of these Terms, you must not access or use our Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. Description of Service</h2>
              <p>
                Konvo AI is a voice-activated artificial intelligence assistant. Our Service may include, but is not limited to, performing tasks, answering questions, providing information, controlling smart devices, making recommendations, and other functionalities as developed and introduced by us from time to time. We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time without prior notice.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. User Accounts and Registration</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-emerald-300 mb-2">a. Eligibility:</h3>
                  <p>You must be at least 18 years old, or the age of majority in your jurisdiction, whichever is higher, to use our Service. If you are under 18, you may only use the Service with the consent and supervision of a parent or legal guardian.</p>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-emerald-300 mb-2">b. Account Creation:</h3>
                  <p>To access certain features, you may be required to register for an account. You agree to provide accurate, current, and complete information during the registration process.</p>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-emerald-300 mb-2">c. Account Security:</h3>
                  <p>You are responsible for safeguarding your account password and for all activities that occur under your account. You must notify us immediately of any unauthorized use of your account.</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. User Conduct and Responsibilities</h2>
              <p className="mb-4">You agree not to use the Service to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Violate any applicable local, state, national, or international law or regulation.</li>
                <li>Infringe upon the intellectual property rights, privacy rights, or other rights of any third party.</li>
                <li>Transmit any material that is abusive, harassing, tortious, defamatory, vulgar, obscene, libelous, invasive of another's privacy, hateful, or racially, ethnically, or otherwise objectionable.</li>
                <li>Use the Service for any fraudulent or misleading purpose.</li>
                <li>Interfere with or disrupt the integrity or performance of the Service or the data contained therein.</li>
                <li>Attempt to gain unauthorized access to the Service, other accounts, or computer systems/networks connected to the Service.</li>
                <li>Use any robot, spider, scraper, or other automated means to access the Service for any purpose without our express written permission.</li>
                <li>Use the Service to generate content that is harmful, dangerous, or that promotes violence, terrorism, or self-harm.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. Intellectual Property Rights</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-emerald-300 mb-2">a. Our IP:</h3>
                  <p>The Service, including its original content, features, functionality, source code, design, and all intellectual property rights therein, are and will remain the exclusive property of Konvo AI and its licensors. Our trademarks and trade dress may not be used without our prior written consent.</p>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-emerald-300 mb-2">b. Your Content:</h3>
                  <p>By using the Service, you may provide input (e.g., voice commands, text) and receive output generated by the Service. You retain any copyright and other rights you already hold in your input. By providing input, you grant Konvo AI a worldwide, perpetual, royalty-free license to use, store, reproduce, modify, and create derivative works from such input to operate, improve, and develop our Service. This includes using your data to train our AI models, as detailed in our Privacy Policy.</p>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-emerald-300 mb-2">c. AI-Generated Output:</h3>
                  <p>The output generated by the Service is provided for your personal, non-commercial use. Konvo AI assigns to you all its right, title, and interest in the output. However, you may not use output in a way that violates these Terms, infringes on any third-party rights, or for any illegal purpose. You are responsible for ensuring the output is appropriate for your use case.</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. Privacy</h2>
              <p>
                Your privacy is critically important to us. Our collection, use, and disclosure of your personal information, including voice data, is governed by our Privacy Policy, which is incorporated into these Terms by reference. Please review our Privacy Policy to understand our practices.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Termination</h2>
              <p>
                We may suspend or terminate your account and access to the Service at our sole discretion, at any time, with or without cause, and with or without notice. Upon termination, your right to use the Service will immediately cease. All provisions of these Terms which by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8. Disclaimers of Warranty</h2>
              <p>
                THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT ANY WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. KONVO AI EXPLICITLY DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">9. Limitation of Liability</h2>
              <p className="mb-4">
                TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL KONVO AI, ITS DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM (I) YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE THE SERVICE; (II) ANY CONDUCT OR CONTENT OF ANY THIRD PARTY ON THE SERVICE; (III) ANY CONTENT OBTAINED FROM THE SERVICE; AND (IV) UNAUTHORIZED ACCESS, USE, OR ALTERATION OF YOUR TRANSMISSIONS OR CONTENT.
              </p>
              <p>
                OUR TOTAL AGGREGATE LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THESE TERMS OR YOUR USE OF THE SERVICE SHALL NOT EXCEED THE AMOUNT OF ONE HUNDRED INDIAN RUPEES (INR 100) OR THE AMOUNT YOU PAID US IN THE PAST SIX MONTHS, WHICHEVER IS GREATER.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">10. Indemnification</h2>
              <p>
                You agree to defend, indemnify, and hold harmless Konvo AI and its licensees and licensors, and their employees, contractors, agents, officers, and directors, from and against any and all claims, damages, obligations, losses, liabilities, costs or debt, and expenses (including but not limited to attorney's fees), resulting from or arising out of a) your use and access of the Service, by you or any person using your account; b) a breach of these Terms, or c) your violation of any third-party right.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">11. Governing Law and Dispute Resolution</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-emerald-300 mb-2">a. Governing Law:</h3>
                  <p>These Terms shall be governed and construed in accordance with the laws of the Republic of India, without regard to its conflict of law provisions.</p>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-emerald-300 mb-2">b. Dispute Resolution:</h3>
                  <p>Any dispute, controversy, or claim arising out of or relating to these Terms, or the breach, termination, or invalidity thereof, shall be first attempted to be resolved through amicable negotiations. If the dispute is not resolved within thirty (30) days, the parties agree to submit the dispute to the exclusive jurisdiction of the courts located in Hyderabad, Telangana, India.</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">12. Changes to Terms</h2>
              <p>
                We reserve the right, at our sole discretion, to modify or replace these Terms at any time. We will provide notice of material changes by posting the new Terms on this page and updating the "Last Updated" date. Your continued use of the Service after any such changes constitutes your acceptance of the new Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">13. Contact Us</h2>
              <p className="mb-4">
                If you have any questions about these Terms, please contact us at:
              </p>
              <div className="mt-4 p-4 bg-white/5 rounded-lg border border-white/10">
                <p className="font-medium text-emerald-300">Konvo AI</p>
                <p>7th floor, T-hub, Raidurgam panmaktha Hyderabad Knowledge City, Serilingampally, Hyderabad, Telangana 500081</p>
                <p>Email: info@konvoai.tech</p>
              </div>
            </section>

            <div className="mt-8 pt-6 border-t border-white/20">
              <p className="text-sm text-gray-400 text-center">
                Last updated: October 2025
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  )
}

export default TermsOfService